import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@interacta-shared/data-access-configuration';
import { Index, PaginatedList, toPaginatedList } from '@interacta-shared/util';
import { IAttachment } from '@interacta/util-post';
import { AIConvoAgent } from '@modules/ai-convo/models/ai-convo-agent.model';
import {
    AIConvoContext,
    AIConvoContextType,
} from '@modules/ai-convo/models/ai-convo-context.model';
import { AIConvoHistoryItem } from '@modules/ai-convo/models/ai-convo-history.model';
import {
    AIConvoGenericMessage,
    AIConvoModelMessage,
} from '@modules/ai-convo/models/ai-convo-message.model';
import { AIConvoModelResponse } from '@modules/ai-convo/models/ai-convo-response.model';
import {
    AIConvoAskChannel,
    AIConvoThinkingTransport,
} from '@modules/ai-convo/models/ai-convo-thinking-transport.model';
import {
    toAIConvoAgent,
    toAIConvoAskChannel,
    toAIConvoHistoryItem,
    toAIConvoHistoryMessage,
    toAIConvoThinkingTransport,
} from '@modules/ai-convo/models/ai-convo.deserialize';
import { toConversationalContext } from '@modules/ai-convo/models/ai-convo.serialize';
import { toAISharedModelMessage } from '@modules/ai-shared/models/ai-shared.deserialize';
import {
    AISharedGenericReference,
    AISharedRole,
    AISharedSmartActionId,
} from '@modules/ai-shared/models/ai-shared.model';
import { map, Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AIConvoService {
    private readonly http = inject(HttpClient);

    private readonly baseUrl = `${inject(ENVIRONMENT).apiBasePath.common}/internal/v4/ai`;

    fetchAgents(): Observable<AIConvoAgent[]> {
        return this.http
            .get<{ list: unknown[] }>(`${this.baseUrl}/agents`)
            .pipe(map((res) => res.list.map(toAIConvoAgent)));
    }

    fetchHistory(
        pageToken?: string,
        pageSize = 20,
    ): Observable<PaginatedList<AIConvoHistoryItem>> {
        return this.http
            .post<any>(`${this.baseUrl}/conversational/sessions`, {
                pageToken,
                pageSize,
                calculateTotalItemsCount: true,
            })
            .pipe(
                map((record) => toPaginatedList(record, toAIConvoHistoryItem)),
            );
    }

    fetchSessionMessages(
        historyId: number,
        pageToken?: string,
        pageSize = 20,
    ): Observable<PaginatedList<AIConvoGenericMessage>> {
        return this.http
            .post<any>(
                `${this.baseUrl}/conversational/session/${historyId}/messages`,
                {
                    pageToken,
                    pageSize,
                    calculateTotalItemsCount: true,
                },
            )
            .pipe(
                map((record) =>
                    toPaginatedList(record, toAIConvoHistoryMessage),
                ),
            );
    }

    renameSession(historyId: number, title: string): Observable<void> {
        return this.http.put<void>(
            `${this.baseUrl}/conversational/session/${historyId}/title`,
            { title },
        );
    }

    deleteSession(historyId: number): Observable<void> {
        return this.http.delete<void>(
            `${this.baseUrl}/conversational/session/${historyId}`,
        );
    }

    fetchThinkingTransport(): Observable<AIConvoThinkingTransport> {
        return this.http
            .post<unknown>(
                `${this.baseUrl}/conversational/auth/create-client-transport`,
                null,
            )
            .pipe(map(toAIConvoThinkingTransport));
    }

    askQuestion(
        communityIds: number[] | null,
        question: string,
        historyId: number | null,
        context: AIConvoContext | null,
        attachmentId: IAttachment['id'] | null,
        referenceId: AISharedGenericReference['id'] | null,
        smartActionId: AISharedSmartActionId | null,
    ): Observable<AIConvoAskChannel> {
        return this.http
            .post<unknown>(`${this.baseUrl}/conversational/ask`, {
                question,
                sessionId: historyId,
                communityIds:
                    context?.type === AIConvoContextType.POST
                        ? null
                        : communityIds,
                attachmentId,
                // The route accepts sources only together with a conversation.
                sourceIds:
                    referenceId != null && historyId !== null
                        ? [referenceId]
                        : [],
                smartActionId,
                context: toConversationalContext(context),
            })
            .pipe(map(toAIConvoAskChannel));
    }

    /**
     * The answer the channel never delivered, if the backend saved it: the newest
     * message of the conversation when it is an answer the chat does not have yet.
     */
    recoverLatestAnswer(
        historyId: number,
        knownMessageIds: Index[],
    ): Observable<AIConvoModelMessage | null> {
        return this.fetchSessionMessages(historyId, undefined, 1).pipe(
            map(({ list }) => {
                const latest = list[0];
                return latest?.role === AISharedRole.MODEL &&
                    !knownMessageIds.includes(latest.id)
                    ? latest
                    : null;
            }),
        );
    }

    fetchResponseFromAIConvo(
        agentId: number,
        communityIds: number[] | null,
        question: string,
        sessionId: string | null,
        context: AIConvoContext | null,
        attachmentId: IAttachment['id'] | null,
        referenceId: AISharedGenericReference['id'] | null,
        smartActionId: AISharedSmartActionId | null,
    ): Observable<AIConvoModelResponse> {
        return this.http
            .post(`${this.baseUrl}/conversational/message`, {
                question: question,
                sessionId,
                // A post context already scopes the question to that post: the
                // community filter chosen elsewhere is no longer meaningful, and
                // its chip is not rendered here, so the user could not clear it.
                communityIds:
                    context?.type === AIConvoContextType.POST
                        ? null
                        : communityIds,
                attachmentId,
                sourceIds: referenceId != null ? [referenceId] : [],
                smartActionId,
                context: toConversationalContext(context),
                agentId,
            })
            .pipe(
                map((result: any) => ({
                    answer: toAISharedModelMessage(result.answer),
                    sessionId: result.sessionId,
                    agent: result?.agent
                        ? toAIConvoAgent(result.agent)
                        : undefined,
                })),
            );
    }
}
